import { EmptyArray } from "../domain/empty/model";
import { ProductsRepositoryImplementation } from "../infrastructure/products-repository-implementation";

import { Product } from "../domain/products/model";

import { cacheManager } from '../infrastructure/cache-manager';
import { getCookie } from "../utils/http-client";

const repo = new ProductsRepositoryImplementation();
const CACHE_PREFIX = 'products';

async function uploadProductInventory(uid: string, cid: string | null, data: unknown) {
    const response = await repo.uploadProductInventory(uid, cid, data).toPromise();
    if (!response) throw new Error('No response from repository');
    cacheManager.invalidate(CACHE_PREFIX);
    return response.data;
}

async function getProductById(uid: string, cid: string | null, id: string) {
    const cacheKey = `${CACHE_PREFIX}-detail-${id}`;
    const cached = cacheManager.getItem(cacheKey);
    if (cached) return cached;

    const response = await repo.getProductById(uid, cid, id).toPromise();
    if (!response) return EmptyArray;
    const data = response.data;
    if (Array.isArray(data) && data.length === 0) return EmptyArray;

    cacheManager.setItem(cacheKey, data);
    return data;
}

async function getAllProducts(
    uid: string,
    cid: string | null,
    query = "",
    page = 1,
    limit = 50,
    cache = true,
    onlyEnabled = false,
    onlyNegativeStock = false,
    hasTax: boolean | null = null,
    stockStatus: string | null = null,
    hasWarranty: boolean | null = null,
    enable: boolean | null = null
) {
    const branchId = getCookie('branch_id') || 'default';
    const cacheKey = `${CACHE_PREFIX}_page_${page}_limit_${limit}_q_${query}_branch_${branchId}_enabled_${onlyEnabled}_neg_${onlyNegativeStock}_tax_${hasTax}_status_${stockStatus}_warr_${hasWarranty}_enable_${enable}`;

    if (cache) {
        const cached = cacheManager.getItem(cacheKey);
        if (cached) return cached;
    }

    const response = await repo.getAllProducts(uid, cid, query, page, limit, onlyEnabled, onlyNegativeStock, hasTax, stockStatus, hasWarranty, enable).toPromise();
    
    // Normalización robusta de la respuesta
    let normalizedResponse: { data: any[], total: number, page: number, page_size: number } = { 
        data: [], 
        total: 0, 
        page, 
        page_size: limit 
    };
    
    if (response && response.data) {
        const rawData = response.data as any;
        if (Array.isArray(rawData)) {
            normalizedResponse = { data: rawData, total: rawData.length, page, page_size: limit };
        } else if (rawData.data && Array.isArray(rawData.data)) {
            normalizedResponse = { 
                data: rawData.data, 
                total: rawData.total || rawData.data.length,
                page: rawData.page || page,
                page_size: rawData.page_size || limit
            };
        } else if (rawData.results && Array.isArray(rawData.results)) {
            normalizedResponse = { 
                data: rawData.results, 
                total: rawData.count || rawData.total || rawData.results.length,
                page,
                page_size: limit
            };
        }
    }

    if (cache && normalizedResponse.data.length > 0) {
        cacheManager.setItem(cacheKey, normalizedResponse);
    }

    return normalizedResponse;
}

async function editProduct(uid: string, cid: string | null, product: Partial<Product>, id: string | number) {
    const response = await repo.editProduct(uid, cid, product, id).toPromise();
    if (!response) throw new Error('No response from repository');
    cacheManager.invalidate(CACHE_PREFIX);
    return response.data;
}

async function addProduct(uid: string, cid: string | null, product: Partial<Product>) {
    const response = await repo.createProduct(uid, cid, product).toPromise();
    if (!response) throw new Error('No response from repository');
    cacheManager.invalidate(CACHE_PREFIX);
    return response.data;
}

async function isEnable(uid: string, cid: string | null, id: string | number, is_enable: boolean) {
    const response = await repo.isEnable(uid, cid, id, is_enable).toPromise();
    if (!response) throw new Error('No response from repository');
    cacheManager.invalidate(CACHE_PREFIX);
    return response.data;
}

async function checkProductDuplicate(uid: string, cid: string | null, value: string, currentProductId: string | number | null = null) {
    if (!value) return null;
    
    // Llamamos al nuevo endpoint especializado (Exact Match)
    const response = await repo.checkProductDuplicate(uid, cid, value).toPromise();
    if (!response || !response.data) return null;

    const duplicate: any = response.data;
    
    // Si estamos editando, ignoramos si el duplicado es nosotros mismos
    if (currentProductId && String(duplicate.id) === String(currentProductId)) return null;

    return duplicate;
}

async function matchProducts(uid: string, cid: string | null, items: Array<{ sku?: string; description: string }>) {
    const response = await repo.matchProducts(uid, cid, items).toPromise();
    if (!response) throw new Error('No response from repository');
    return response.data;
}

async function deleteProduct(uid: string, cid: string | null, id: string | number) {
    const response = await repo.deleteProduct(uid, cid, id).toPromise();
    if (!response) throw new Error('No response from repository');
    cacheManager.invalidate(CACHE_PREFIX);
    return response.data;
}

export { getAllProducts, editProduct, addProduct, getProductById, uploadProductInventory, isEnable, checkProductDuplicate, matchProducts, deleteProduct };

